import { useState } from 'react'
import { toast } from 'react-toastify'

import {
    uploadFacilitiesInspectionSheet,
    validateFacilitiesInspectionFile,
    validateFacilitiesInspectionFileSize,
    validateEmail,
    UploadFacilitiesInspectionRequest,
    UploadFacilitiesInspectionResponse,
} from '../helpers/inspectionUpload'

export const useFacilitiesInspectionUpload = () => {
    const [isUploading, setIsUploading] = useState(false)
    const [uploadProgress, setUploadProgress] = useState(0)

    /**
     * Upload a facilities inspection sheet
     * @param request - The upload request containing file and email
     * @returns Promise with upload response
     */
    const uploadSheet = async (
        request: UploadFacilitiesInspectionRequest,
    ): Promise<UploadFacilitiesInspectionResponse> => {
        setIsUploading(true)
        setUploadProgress(0)

        try {
            // Validate file type
            if (!validateFacilitiesInspectionFile(request.file)) {
                throw new Error(
                    'Please select a valid file type (CSV, XLSX, or XLS)',
                )
            }

            // Validate file size
            if (!validateFacilitiesInspectionFileSize(request.file)) {
                throw new Error('File size must be less than 10MB')
            }

            // Validate email
            if (!validateEmail(request.email)) {
                throw new Error('Please enter a valid email address')
            }

            // Simulate progress (since we can't track actual upload progress with current API)
            const progressInterval = setInterval(() => {
                setUploadProgress((prev) => {
                    if (prev >= 90) {
                        clearInterval(progressInterval)
                        return 90
                    }
                    return prev + 10
                })
            }, 200)

            const result = await uploadFacilitiesInspectionSheet({
                file: request.file,
                email: request.email.trim(),
            })

            clearInterval(progressInterval)
            setUploadProgress(100)

            toast.success(result.message)
            return result
        } catch (error: any) {
            toast.error(
                error.message || 'Error uploading facilities inspection sheet',
            )
            throw error
        } finally {
            setIsUploading(false)
            // Reset progress after a delay
            setTimeout(() => setUploadProgress(0), 1000)
        }
    }

    /**
     * Validate file for facilities inspection upload
     * @param file - The file to validate
     * @returns boolean indicating if file is valid
     */
    const validateFile = (file: File): boolean => {
        return (
            validateFacilitiesInspectionFile(file) &&
            validateFacilitiesInspectionFileSize(file)
        )
    }

    /**
     * Validate email format
     * @param email - The email to validate
     * @returns boolean indicating if email is valid
     */
    const validateEmailFormat = (email: string): boolean => {
        return validateEmail(email)
    }

    return {
        uploadSheet,
        validateFile,
        validateEmailFormat,
        isUploading,
        uploadProgress,
    }
}
